import React from 'react';
import axios from 'axios';
import { ComposedChart, Line, Scatter, ReferenceLine, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { T, MONO, tint } from './poyais-ui/theme';
import { Icon } from './poyais-ui/icons';
import { useIsMobile } from './poyais-ui/hooks';
import { useLang, addTranslations } from './i18n';
import { GrowthStyles, GrowthHero, SectionCard, Skeleton, ErrorState, LegendDot, cardStyle, tooltipStyle } from './GrowthUI';

addTranslations({
  'Fiyat & Talep': 'Price & Demand',
  'Her ürün için gelirini en çok artıracak fiyatı bul.': 'Find the price that grows revenue the most for each product.',
  'Son 12 ayın gerçek satış verisiyle fiyat değiştiğinde talebin nasıl tepki verdiğini ölçer.': 'Measures how demand reacts to price changes using the last 12 months of real sales data.',
  'ürün': 'products',
  'Ürün ara...': 'Search products...',
  'Toplam Gelir': 'Total Revenue',
  'Mevcut Fiyat': 'Current Price',
  'adet': 'units',
  'Son Ay Talep': 'Last Month Demand',
  'Esneklik Katsayısı': 'Elasticity Coefficient',
  'Esnek': 'Elastic',
  'Esnek Değil': 'Inelastic',
  'Anomali': 'Anomaly',
  'Yetersiz veri': 'Not enough data',
  'Talep fiyat değişimlerine oldukça duyarlı.': 'Demand is highly sensitive to price changes.',
  'Talep fiyat değişimlerine görece duyarsız.': 'Demand is relatively insensitive to price changes.',
  'Pozitif esneklik. Muhtemelen anomali/sezonluk etki, doğrudan fiyat etkisi değil.': 'Positive elasticity. Likely an anomaly/seasonal effect, not a direct price effect.',
  'Optimum Fiyat': 'Optimum Price',
  'mevcuda göre': 'vs current',
  'Geliri maksimize eden fiyat (LOESS modeli).': 'Revenue-maximizing price (LOESS model).',
  'Gelir Etkisi': 'Revenue Impact',
  'tahmini/ay': 'est. monthly',
  'Optimum noktadan fiyatlandırılırsa.': 'If pricing at optimum point.',
  'Öneri': 'Recommendation',
  'Fiyatı {from} → {to} olarak artır': 'Raise the price from {from} to {to}',
  'Fiyatı {from} → {to} olarak düşür': 'Lower the price from {from} to {to}',
  'Mevcut fiyat optimum noktada': 'The current price is at the optimum',
  'Model, bu değişikliğin aylık geliri tahmini %{pct} (≈ {amount}) artırabileceğini öngörüyor.': 'The model estimates this change could lift monthly revenue by {pct}% (≈ {amount}).',
  'Fiyatı değiştirmek geliri anlamlı ölçüde artırmıyor; mevcut fiyatı korumak mantıklı.': 'Changing the price does not meaningfully increase revenue; keeping the current price makes sense.',
  'Geçmiş veriye dayalı bir tahmindir; değişikliği kademeli uygulayıp sonucu izle.': 'This is an estimate based on past data; apply the change gradually and monitor the result.',
  'Fiyat ve Talep Zaman Çizelgesi': 'Price vs Demand Timeline',
  'Aylık ortalama fiyat ve satılan adet.': 'Monthly average price and units sold.',
  'Fiyat': 'Price',
  'Talep': 'Demand',
  'Optimum': 'Optimum',
  'Fiyat Optimizasyon Eğrisi': 'Price Optimization Curve',
  'Gözlenen fiyat aralığında gelir ve talep tepkisi.': 'Revenue and demand response across observed price range.',
  'Gelir': 'Revenue',
  'Bu grafik nasıl okunur?': 'How to read this chart?',
  'Mavi eğri, farklı fiyatlarda tahmini aylık geliri; yeşil kesikli çizgi tahmini talebi gösterir. Turuncu çizgi, geliri en yükselten fiyattır (±güvenlik bandı içinde).': 'The blue curve shows estimated monthly revenue at each price; the dashed green line shows estimated demand. The amber line is the price that maximizes revenue (within the ± guardrail).',
  'Model nasıl çalışıyor?': 'How does the model work?',
  'Gerçek aylık noktalar ve model eğrisi, adım adım hesaplama ve metodoloji.': 'Actual monthly points and the model curve, step-by-step calculation and methodology.',
  'Veri Noktaları:': 'Data Points:',
  'Her nokta bir ayın gerçek fiyat ve gelir performansını temsil eder.': "Each dot represents one month's actual price and revenue performance.",
  'Eğri Uydurma:': 'Curve Fitting:',
  'LOESS (Yerel Regresyon) dağılımdan geçen düzgün bir eğilim çizer, "bu fiyatı denesek ne olurdu?" sorusuna cevap arar.': 'LOESS (Local Regression) draws a smooth trend through the scatter, answering "what if we tried this price?"',
  'Optimum:': 'Optimum:',
  'Gelir eğrisinin tepe noktası, geliri maksimize eden fiyat noktasıdır (±%{pct} güvenlik bandı içinde).': 'The peak of the revenue curve is the price point that maximizes income (within a ±{pct}% safety band).',
  'Mevcut': 'Current',
  'Aylık Esneklik': 'Monthly Elasticity',
  'Aydan aya fiyat ve talep değişimi.': 'Month-over-month price and demand change.',
  'Ay': 'Month',
  'Δ Fiyat': 'Δ Price',
  'Δ Talep': 'Δ Demand',
  'Esneklik': 'Elasticity',
  'Metodoloji': 'Methodology',
  'Veri:': 'Data:',
  'Aylık fiyat = Gelir / Adet, talep = satılan adet.': 'Monthly price = Revenue / Units, demand = Units sold.',
  'Esneklik:': 'Elasticity:',
  'Log-log OLS —': 'Log-log OLS —',
  'Optimum Fiyat:': 'Optimum Price:',
  '(P, R=P×Q) üzerinde LOESS düzgünleştirme, ±%10-20 güvenlik bandı içinde tepe.': 'LOESS smoothing on (P, R=P×Q), peak within ±10-20% guardrail.',
  'Etki:': 'Impact:',
  'LOESS:': 'LOESS:',
  'Tricube çekirdek ve yenilik ağırlıklandırmasıyla (λ={lambda}) yerel doğrusal regresyon.': 'Local linear regression with tricube kernel and recency weighting (λ={lambda}).',
  'Güvenlik Bantları:': 'Guardrails:',
  'Son ay geliri P75 üzerindeyse ±%10, değilse ±%20.': '±10% if last month revenue ≥ P75, else ±20%.',
  'Güven:': 'Confidence:',
  'Negatif esneklik = beklenen (yeşil). Pozitif = anomali/sezonluk (kırmızı).': 'Negative elasticity = expected (green). Positive = anomaly/seasonality (red).',
  'Not:': 'Note:',
  'Geçmiş performans gelecek sonuçları garanti etmez.': 'Past performance does not guarantee future results.',
  'Veri yüklenemedi.': 'Could not load data.',
  'Ürün bulunamadı.': 'No products found.',
  'Aramanı değiştirip tekrar dene.': 'Try a different search.',
  'Bu ürün için yeterli veri yok.': 'Not enough data for this product.',
  'Soldan bir ürün seçin.': 'Select a product on the left.',
  'Ürün değiştir': 'Change product',
  'Kapat': 'Close',
  'Seçili ürün': 'Selected product',
  'Ürünü aç': 'Open product',
  'Duyarlı': 'Sensitive',
  'Duyarsız': 'Insensitive',
  'gösteriliyor': 'shown',
});

const API = '/api/v1';
const PAGE_SIZE = 40;
// Sabit kur/switch yok — Analytics'te mağazanın fiilen sattığı para birimi
// (GA4 currencyCode) backend'den geliyor, burada sadece sembole çevriliyor.
const CURRENCY_SYMBOLS: Record<string, string> = { TRY: '₺', USD: '$', EUR: '€', GBP: '£' };

type ProductRow = {
  id: string; name: string; category: string; image_url?: string | null; product_url?: string | null;
  current_price: number; revenue_total: number; last_demand: number;
  elasticity: number | null; elastic: boolean | null;
};
type ProductsResponse = { meta: { total: number; page: number; page_size: number; total_pages: number; months: number; currency: string }; products: ProductRow[] };

type TimelinePoint = { month: string; price: number; demand: number; revenue: number };
type CurvePoint = { price: number; revenue: number; demand?: number | null };
type MonthlyElasticityRow = { month: string; price: number; demand: number; revenue: number; d_price_pct: number; d_demand_pct: number; elasticity: number };
type DetailResponse = {
  currency: string;
  product: { id: string; name: string; category: string; image_url?: string | null; product_url?: string | null };
  summary: { total_revenue: number; current_price: number; last_demand: number };
  elasticity: { coefficient: number | null; elastic: boolean | null; expected: boolean };
  optimum: { price: number; vs_current_pct: number | null; revenue_impact_pct: number | null; revenue_impact_monthly: number; guardrail_pct: number };
  timeline: TimelinePoint[];
  optimization_curve: CurvePoint[];
  model_visualization: { curve: { price: number; revenue: number }[]; points: { price: number; revenue: number }[]; current_price: number; optimum_price: number };
  monthly_elasticity: MonthlyElasticityRow[];
  methodology: { lambda_recency: number; guardrail_pct: number; min_points: number };
};

function money(v: number, currency: string) {
  const symbol = CURRENCY_SYMBOLS[currency] || (currency ? currency + ' ' : '₺');
  return symbol + Math.round(v || 0).toLocaleString('tr-TR');
}

function monthLabel(m: string, lang: string) {
  const d = new Date(m + 'T00:00:00');
  return d.toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US', { month: 'short', year: '2-digit' });
}

function useDebounced<T>(value: T, delay = 350): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const h = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return debounced;
}

// Esneklik → etiket + renk. Negatif beklenen yön; |β|>1 esnek.
function elasticityTag(e: number | null) {
  if (e == null) return { label: 'Yetersiz veri', color: T.textDim, bg: T.subtleBg };
  if (e > 0) return { label: 'Anomali', color: T.redText, bg: T.redTint };
  if (Math.abs(e) > 1) return { label: 'Esnek', color: T.blueText, bg: T.blueTint };
  return { label: 'Esnek Değil', color: T.greenText, bg: T.greenTint };
}

function TimelineTooltip({ active, payload, label, currency, lang }: any) {
  const { t } = useLang();
  if (!active || !payload?.length) return null;
  const price = payload.find((p: any) => p.dataKey === 'price')?.value;
  const demand = payload.find((p: any) => p.dataKey === 'demand')?.value;
  return (
    <div style={tooltipStyle}>
      <div style={{ fontWeight: 800, color: T.text, marginBottom: 4 }}>{monthLabel(label, lang)}</div>
      {price != null && <div style={{ color: T.blueText, fontWeight: 600 }}>{t('Fiyat')}: {money(price, currency)}</div>}
      {demand != null && <div style={{ color: T.greenText, fontWeight: 600 }}>{t('Talep')}: {demand} {t('adet')}</div>}
    </div>
  );
}

function CurveTooltip({ active, payload, currency, t }: any) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div style={tooltipStyle}>
      <div style={{ fontWeight: 800, color: T.text, marginBottom: 4 }}>{money(p.price, currency)}</div>
      {p.revenue != null && <div style={{ color: T.blueText, fontWeight: 600 }}>{t('Gelir')}: {money(p.revenue, currency)}</div>}
      {p.demand != null && <div style={{ color: T.greenText, fontWeight: 600 }}>{t('Talep')}: {Math.round(p.demand)} {t('adet')}</div>}
    </div>
  );
}

function Thumb({ src, size }: { src?: string | null; size: number }) {
  const [broken, setBroken] = React.useState(false);
  if (src && !broken) {
    return <img src={src} alt="" width={size} height={size} onError={() => setBroken(true)}
      style={{ borderRadius: Math.round(size * 0.24), objectFit: 'cover', flexShrink: 0, border: `1px solid ${T.border}`, background: T.subtleBg }} />;
  }
  return (
    <span style={{ width: size, height: size, borderRadius: Math.round(size * 0.24), background: T.subtleBg, color: T.textFaint,
      flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={Math.round(size * 0.46)} height={Math.round(size * 0.46)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="16" rx="3"/><circle cx="9" cy="10" r="1.8"/><path d="M21 16l-5-5-8 8"/>
      </svg>
    </span>
  );
}

function ElasticityPill({ e, small }: { e: number | null; small?: boolean }) {
  const { t } = useLang();
  const tag = elasticityTag(e);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: small ? 10.5 : 11.5, fontWeight: 800, color: tag.color,
      background: tag.bg, borderRadius: 999, padding: small ? '2px 7px' : '3px 10px', whiteSpace: 'nowrap' }}>
      {e != null && <span style={{ fontFamily: MONO }}>{e.toFixed(2)}</span>}
      {!small || e == null ? t(tag.label) : null}
    </span>
  );
}

function ProductListItem({ p, active, currency, onClick }: { p: ProductRow; active: boolean; currency: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className={active ? undefined : 'gr-row'} style={{
      display: 'flex', alignItems: 'center', gap: 11, width: '100%', padding: '10px 12px', borderRadius: 12,
      border: `1px solid ${active ? tint(T.accent, 55) : 'transparent'}`, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
      background: active ? tint(T.accent, 12) : 'transparent', transition: 'background .12s', marginBottom: 2,
    }}>
      <Thumb src={p.image_url} size={38} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.name}>{p.name}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, minWidth: 0 }}>
          <span style={{ fontSize: 11, color: T.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{p.category}</span>
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text, fontFamily: MONO }}>{money(p.current_price, currency)}</div>
        <ElasticityPill e={p.elasticity} small />
      </div>
    </button>
  );
}

// Ürün listesi: arama + sonsuz kaydırma. Masaüstünde ekranın yüksekliği boyunca
// yapışkan kalır; mobilde açılır-kapanır seçici içinde kullanılır.
function ProductPicker({ search, setSearch, items, total, currency, loading, loadingMore, error, hasMore, onLoadMore, selected, onSelect, listStyle }: {
  search: string; setSearch: (v: string) => void; items: ProductRow[]; total: number | null; currency: string;
  loading: boolean; loadingMore: boolean; error: string | null; hasMore: boolean; onLoadMore: () => void;
  selected: string | null; onSelect: (id: string) => void; listStyle: React.CSSProperties;
}) {
  const { t } = useLang();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const onScroll = () => {
    const el = scrollRef.current;
    if (el && hasMore && !loadingMore && el.scrollTop + el.clientHeight > el.scrollHeight - 160) onLoadMore();
  };
  // Liste kutuyu doldurmuyorsa (uzun ekran) kaydırma olmadan da sonraki sayfayı çek.
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el && hasMore && !loading && !loadingMore && el.scrollHeight <= el.clientHeight + 40) onLoadMore();
  }, [items.length, hasMore, loading, loadingMore, onLoadMore]);

  return (
    <>
      <div style={{ padding: 12, borderBottom: `1px solid ${T.divider}`, flexShrink: 0 }}>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: T.textDim, display: 'flex' }}><Icon.search size={15} /></span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('Ürün ara...')}
            style={{ width: '100%', padding: '10px 12px 10px 34px', borderRadius: 11, border: `1px solid ${T.borderMid}`, background: T.surface,
              color: T.text, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
        </div>
        {total != null && (
          <div style={{ fontSize: 11, color: T.textDim, marginTop: 8, fontWeight: 600 }}>
            {total.toLocaleString('tr-TR')} {t('ürün')}{total > items.length ? ` · ${items.length} ${t('gösteriliyor')}` : ''}
          </div>
        )}
      </div>
      <div ref={scrollRef} onScroll={onScroll} className="gr-thin" style={{ overflowY: 'auto', padding: 6, ...listStyle }}>
        {loading && Array.from({ length: 8 }).map((_, i) => (
          <div key={i} style={{ display: 'flex', gap: 11, alignItems: 'center', padding: '10px 12px' }}>
            <Skeleton h={38} w={38} r={9} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}><Skeleton h={11} w="80%" /><Skeleton h={9} w="45%" /></div>
          </div>
        ))}
        {!loading && error && <div style={{ padding: 24, textAlign: 'center', color: T.textDim, fontSize: 12.5 }}>{error}</div>}
        {!loading && !error && items.length === 0 && (
          <div style={{ padding: '32px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{t('Ürün bulunamadı.')}</div>
            <div style={{ fontSize: 12, color: T.textDim, marginTop: 4 }}>{t('Aramanı değiştirip tekrar dene.')}</div>
          </div>
        )}
        {!loading && !error && items.map(p => (
          <ProductListItem key={p.id} p={p} active={selected === p.id} currency={currency} onClick={() => onSelect(p.id)} />
        ))}
        {loadingMore && <div style={{ padding: 12, display: 'flex', justifyContent: 'center' }}><Skeleton h={10} w={90} /></div>}
      </div>
    </>
  );
}

function ElasticityScale({ e }: { e: number | null }) {
  const { t } = useLang();
  // -3 … +1 aralığı: [-3,-1] esnek, [-1,0] esnek değil, [0,1] anomali.
  const pos = e == null ? null : (Math.max(-3, Math.min(1, e)) + 3) / 4 * 100;
  return (
    <div style={{ marginTop: 2 }}>
      <div style={{ position: 'relative', height: 8, borderRadius: 999, overflow: 'hidden', display: 'flex' }}>
        <span style={{ flex: 2, background: T.blueTint }} />
        <span style={{ flex: 1, background: T.greenTint }} />
        <span style={{ flex: 1, background: T.redTint }} />
      </div>
      {pos != null && (
        <div style={{ position: 'relative', height: 0 }}>
          <span style={{ position: 'absolute', left: `${pos}%`, top: -11, width: 14, height: 14, marginLeft: -7, borderRadius: '50%',
            background: T.surface, border: `3px solid ${elasticityTag(e).color}`, boxShadow: '0 1px 3px rgba(0,0,0,.15)' }} />
        </div>
      )}
      <div style={{ display: 'flex', fontSize: 10, color: T.textDim, fontWeight: 700, marginTop: 6 }}>
        <span style={{ flex: 2 }}>{t('Duyarlı')}</span>
        <span style={{ flex: 1 }}>{t('Duyarsız')}</span>
        <span style={{ flex: 1, textAlign: 'right' }}>{t('Anomali')}</span>
      </div>
    </div>
  );
}

function Kpi({ icon, color, label, value, valueColor, sub, desc, children }: {
  icon: React.ReactNode; color: string; label: string; value: string; valueColor?: string; sub?: React.ReactNode; desc: string; children?: React.ReactNode;
}) {
  return (
    <div className="gr-hover" style={{ ...cardStyle, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 9, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ width: 32, height: 32, borderRadius: 10, background: tint(color, 14), color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: T.textMid }}>{label}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 26, fontWeight: 800, color: valueColor || T.text, fontFamily: MONO, letterSpacing: '-0.02em' }}>{value}</span>
        {sub}
      </div>
      {children}
      <span style={{ fontSize: 12, color: T.textDim, lineHeight: 1.45 }}>{desc}</span>
    </div>
  );
}

function Recommendation({ detail, currency, isMobile }: { detail: DetailResponse; currency: string; isMobile: boolean }) {
  const { t } = useLang();
  const cur = detail.summary.current_price;
  const opt = detail.optimum.price;
  const diff = opt - cur;
  const impact = detail.optimum.revenue_impact_pct;
  const actionable = Math.abs(diff) > 0.5 && impact != null && impact > 0;
  const up = diff > 0;
  const color = actionable ? (up ? T.green : T.blue) : T.textDim;
  const title = actionable
    ? t(up ? 'Fiyatı {from} → {to} olarak artır' : 'Fiyatı {from} → {to} olarak düşür').replace('{from}', money(cur, currency)).replace('{to}', money(opt, currency))
    : t('Mevcut fiyat optimum noktada');
  const body = actionable
    ? t('Model, bu değişikliğin aylık geliri tahmini %{pct} (≈ {amount}) artırabileceğini öngörüyor.')
        .replace('{pct}', String(Math.abs(impact!)))
        .replace('{amount}', money(Math.abs(detail.optimum.revenue_impact_monthly), currency))
    : t('Fiyatı değiştirmek geliri anlamlı ölçüde artırmıyor; mevcut fiyatı korumak mantıklı.');
  return (
    <div className="gr-fade" style={{ ...cardStyle, marginBottom: 14, padding: isMobile ? 16 : '18px 20px', position: 'relative', overflow: 'hidden',
      background: `linear-gradient(115deg, ${tint(T.accent, 16)} 0%, var(--py-card) 70%)`, borderColor: tint(T.accent, 45),
      display: 'flex', gap: 14, alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row' }}>
      {!isMobile && <span style={{ width: 44, height: 44, borderRadius: 13, background: T.accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          boxShadow: `0 6px 16px ${tint(T.accent, 50)}` }}><Icon.sparkle size={22} sw={2} /></span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: T.accentDeep, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>{t('Öneri')}</div>
        <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 800, color: T.text, letterSpacing: '-0.01em' }}>{title}</div>
        <div style={{ fontSize: 13, color: T.textMid, marginTop: 4, lineHeight: 1.5 }}>{body}</div>
        <div style={{ fontSize: 11.5, color: T.textDim, marginTop: 6 }}>{t('Geçmiş veriye dayalı bir tahmindir; değişikliği kademeli uygulayıp sonucu izle.')}</div>
      </div>
      {actionable && (
        <div style={{ flexShrink: 0, textAlign: isMobile ? 'left' : 'right' }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: T.greenText, fontFamily: MONO, letterSpacing: '-0.02em' }}>+{Math.abs(impact!)}%</div>
          <div style={{ fontSize: 11.5, color: T.textDim, fontWeight: 600 }}>{t('Gelir Etkisi')}</div>
        </div>
      )}
      <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: color }} />
    </div>
  );
}

function DetailSkeleton({ isMobile }: { isMobile: boolean }) {
  return (
    <div>
      <div style={{ ...cardStyle, padding: 18, marginBottom: 14, display: 'flex', gap: 14, alignItems: 'center' }}>
        <Skeleton h={56} w={56} r={14} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}><Skeleton h={16} w="60%" /><Skeleton h={11} w="30%" /></div>
      </div>
      <Skeleton h={96} r={16} style={{ marginBottom: 14 }} />
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12, marginBottom: 14 }}>
        {[0, 1, 2].map(i => <Skeleton key={i} h={130} r={16} />)}
      </div>
      <Skeleton h={320} r={16} />
    </div>
  );
}

export default function GrowthPriceElasticity({ clientId }: { clientId: number }) {
  const { t, lang } = useLang();
  const isMobile = useIsMobile();
  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebounced(search);
  const [items, setItems] = React.useState<ProductRow[]>([]);
  const [meta, setMeta] = React.useState<ProductsResponse['meta'] | null>(null);
  const [listError, setListError] = React.useState<string | null>(null);
  const [listLoading, setListLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [selected, setSelected] = React.useState<string | null>(null);
  const [detail, setDetail] = React.useState<DetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailError, setDetailError] = React.useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const reqId = React.useRef(0);
  const detailTopRef = React.useRef<HTMLDivElement>(null);

  const fetchPage = React.useCallback((page: number) => {
    const id = ++reqId.current;
    return axios.get<ProductsResponse>(`${API}/growth/price-elasticity/products?client_id=${clientId}&months=12&search=${encodeURIComponent(debouncedSearch)}&page=${page}&page_size=${PAGE_SIZE}`)
      .then(res => ({ id, data: res.data }));
  }, [clientId, debouncedSearch]);

  React.useEffect(() => {
    setListLoading(true);
    setListError(null);
    fetchPage(1)
      .then(({ id, data }) => {
        if (id !== reqId.current) return;
        setItems(data.products || []);
        setMeta(data.meta);
        setSelected(s => s ?? data.products?.[0]?.id ?? null);
      })
      .catch(err => setListError(err?.response?.data?.detail || t('Veri yüklenemedi.')))
      .finally(() => setListLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPage]);

  const hasMore = !!meta && meta.page < meta.total_pages;
  const loadMore = React.useCallback(() => {
    if (!meta || loadingMore || meta.page >= meta.total_pages) return;
    setLoadingMore(true);
    fetchPage(meta.page + 1)
      .then(({ id, data }) => {
        if (id !== reqId.current) return;
        setItems(prev => {
          const seen = new Set(prev.map(p => p.id));
          return [...prev, ...(data.products || []).filter(p => !seen.has(p.id))];
        });
        setMeta(data.meta);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }, [meta, loadingMore, fetchPage]);

  React.useEffect(() => {
    if (!selected) return;
    setDetailLoading(true);
    setDetailError(null);
    axios.get(`${API}/growth/price-elasticity/detail?client_id=${clientId}&months=12&product=${encodeURIComponent(selected)}`)
      .then(res => setDetail(res.data))
      .catch(err => setDetailError(err?.response?.data?.detail || t('Bu ürün için yeterli veri yok.')))
      .finally(() => setDetailLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, selected]);

  const selectProduct = (id: string) => {
    setSelected(id);
    if (isMobile) {
      setPickerOpen(false);
      setTimeout(() => detailTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  };

  const currency = detail?.currency || meta?.currency || 'TRY'; // Analytics'in raporladığı gerçek para birimi — sabit varsayım yok.
  const selectedRow = items.find(p => p.id === selected) || null;

  // Liste hata verdiyse (ör. GA4 bağlı değil) sayfanın tamamında tek bir açıklama göster.
  if (!listLoading && listError && items.length === 0 && !debouncedSearch) {
    return <div><GrowthStyles /><ErrorState error={listError} /></div>;
  }

  const pickerProps = {
    search, setSearch, items, total: meta?.total ?? null, currency, loading: listLoading, loadingMore, error: listError,
    hasMore, onLoadMore: loadMore, selected, onSelect: selectProduct,
  };

  const axisTick = { fontSize: 10.5, fill: T.textDim as any };
  const chartH = isMobile ? 240 : 300;

  return (
    <div>
      <GrowthStyles />
      <GrowthHero isMobile={isMobile} color={T.blue} icon={<Icon.target size={24} sw={2} />}
        title={t('Her ürün için gelirini en çok artıracak fiyatı bul.')}
        desc={t('Son 12 ayın gerçek satış verisiyle fiyat değiştiğinde talebin nasıl tepki verdiğini ölçer.')} />

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr)' : '340px minmax(0,1fr)', gap: 16, alignItems: 'stretch' }}>
        {/* Sol: ürün listesi — kart detay sütunu boyunca uzanır, içerik ekranda yapışkan kalır */}
        {isMobile ? (
          <div style={{ ...cardStyle, overflow: 'hidden' }}>
            {!pickerOpen ? (
              <button onClick={() => setPickerOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: 12,
                background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                <Thumb src={selectedRow?.image_url || detail?.product.image_url} size={42} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 800, color: T.textDim, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t('Seçili ürün')}</div>
                  <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedRow?.name || detail?.product.name || (listLoading ? '…' : t('Soldan bir ürün seçin.'))}
                  </div>
                </div>
                <span style={{ fontSize: 12, fontWeight: 800, color: T.accentDeep, background: tint(T.accent, 14), borderRadius: 999, padding: '7px 12px', whiteSpace: 'nowrap',
                  display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Icon.search size={13} sw={2.2} />{t('Ürün değiştir')}
                </span>
              </button>
            ) : (
              <div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 10px 0' }}>
                  <button onClick={() => setPickerOpen(false)} style={{ background: 'none', border: 'none', color: T.textMid, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: 4 }}>{t('Kapat')}</button>
                </div>
                <ProductPicker {...pickerProps} listStyle={{ maxHeight: '58vh' }} />
              </div>
            )}
          </div>
        ) : (
          <div style={{ ...cardStyle, minHeight: 'calc(100vh - 150px)' }}>
            <div style={{ position: 'sticky', top: 72, height: 'calc(100vh - 88px)', maxHeight: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <ProductPicker {...pickerProps} listStyle={{ flex: 1, minHeight: 0 }} />
            </div>
          </div>
        )}

        {/* Sağ: seçili ürün detayı */}
        <div ref={detailTopRef} style={{ minWidth: 0, scrollMarginTop: 80 }}>
          {!selected && !listLoading && (
            <div style={{ ...cardStyle, padding: 60, textAlign: 'center', color: T.textDim, fontSize: 13 }}>{t('Soldan bir ürün seçin.')}</div>
          )}
          {(listLoading && !selected) || (selected && detailLoading) ? <DetailSkeleton isMobile={isMobile} /> : null}
          {selected && !detailLoading && detailError && <ErrorState error={detailError} />}

          {selected && !detailLoading && !detailError && detail && (
            <div className="gr-fade">
              {/* Ürün başlığı */}
              <div style={{ ...cardStyle, padding: isMobile ? 14 : '16px 18px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: '1 1 260px', minWidth: 0 }}>
                  <Thumb src={detail.product.image_url} size={isMobile ? 48 : 56} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: isMobile ? 15 : 17, fontWeight: 800, color: T.text, lineHeight: 1.3, wordBreak: 'break-word' }}>{detail.product.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: T.textMidAlt, background: T.trackBg, borderRadius: 999, padding: '3px 10px' }}>{detail.product.category}</span>
                      <ElasticityPill e={detail.elasticity.coefficient} />
                      {detail.product.product_url && (
                        <a href={detail.product.product_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11.5, fontWeight: 700, color: T.accentDeep, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {t('Ürünü aç')} <Icon.external size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, auto)', gap: isMobile ? 8 : 10, width: isMobile ? '100%' : 'auto' }}>
                  {[
                    { l: t('Toplam Gelir'), v: money(detail.summary.total_revenue, currency) },
                    { l: t('Mevcut Fiyat'), v: money(detail.summary.current_price, currency) },
                    { l: t('Son Ay Talep'), v: `${detail.summary.last_demand} ${t('adet')}` },
                  ].map(s => (
                    <div key={s.l} style={{ background: T.subtleBg, borderRadius: 12, padding: isMobile ? '8px 10px' : '9px 14px', minWidth: 0 }}>
                      <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 800, color: T.text, fontFamily: MONO, whiteSpace: 'nowrap' }}>{s.v}</div>
                      <div style={{ fontSize: 10.5, color: T.textDim, fontWeight: 600, whiteSpace: 'nowrap' }}>{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>

              <Recommendation detail={detail} currency={currency} isMobile={isMobile} />

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12, marginBottom: 14 }}>
                <Kpi icon={<Icon.chart size={16} sw={2.1} />} color={T.green}
                  label={t('Esneklik Katsayısı')}
                  value={detail.elasticity.coefficient != null ? detail.elasticity.coefficient.toFixed(2) : '—'}
                  valueColor={detail.elasticity.expected ? T.greenText : T.redText}
                  desc={detail.elasticity.expected ? (detail.elasticity.elastic ? t('Talep fiyat değişimlerine oldukça duyarlı.') : t('Talep fiyat değişimlerine görece duyarsız.')) : t('Pozitif esneklik. Muhtemelen anomali/sezonluk etki, doğrudan fiyat etkisi değil.')}>
                  <ElasticityScale e={detail.elasticity.coefficient} />
                </Kpi>
                <Kpi icon={<Icon.target size={16} />} color={T.blue}
                  label={t('Optimum Fiyat')}
                  value={money(detail.optimum.price, currency)}
                  sub={detail.optimum.vs_current_pct != null && (
                    <span style={{ fontSize: 12, fontWeight: 800, color: detail.optimum.vs_current_pct < 0 ? T.redText : T.greenText }}>
                      {detail.optimum.vs_current_pct >= 0 ? '+' : ''}{detail.optimum.vs_current_pct}% <span style={{ color: T.textDim, fontWeight: 600 }}>{t('mevcuda göre')}</span>
                    </span>
                  )}
                  desc={t('Geliri maksimize eden fiyat (LOESS modeli).')} />
                <Kpi icon={<Icon.bolt size={16} />} color={T.amber}
                  label={t('Gelir Etkisi')}
                  value={detail.optimum.revenue_impact_pct != null ? `${detail.optimum.revenue_impact_pct >= 0 ? '+' : ''}${detail.optimum.revenue_impact_pct}%` : '—'}
                  valueColor={detail.optimum.revenue_impact_pct != null && detail.optimum.revenue_impact_pct >= 0 ? T.greenText : T.redText}
                  sub={<span style={{ fontSize: 12, fontWeight: 800, color: T.textMid }}>{money(Math.abs(detail.optimum.revenue_impact_monthly), currency)} <span style={{ color: T.textDim, fontWeight: 600 }}>{t('tahmini/ay')}</span></span>}
                  desc={t('Optimum noktadan fiyatlandırılırsa.')} />
              </div>

              <SectionCard title={t('Fiyat ve Talep Zaman Çizelgesi')} sub={t('Aylık ortalama fiyat ve satılan adet.')} style={{ marginBottom: 14 }} pad={isMobile ? 14 : 18}
                right={<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <LegendDot line color={T.blue} label={t('Fiyat')} /><LegendDot line color={T.green} label={t('Talep')} /><LegendDot line dashed color={T.amber} label={t('Optimum')} />
                </div>}>
                <ResponsiveContainer width="100%" height={chartH}>
                  <ComposedChart data={detail.timeline} margin={{ top: 10, right: isMobile ? 0 : 10, bottom: 0, left: isMobile ? -14 : 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.divider as any} vertical={false} />
                    <XAxis dataKey="month" tickFormatter={(m) => monthLabel(m, lang)} tick={axisTick} axisLine={false} tickLine={false} minTickGap={12} />
                    <YAxis yAxisId="price" tickFormatter={(v) => money(v, currency)} tick={{ ...axisTick, fill: T.blueText as any }} axisLine={false} tickLine={false} width={isMobile ? 54 : 66} />
                    <YAxis yAxisId="demand" orientation="right" tick={{ ...axisTick, fill: T.greenText as any }} axisLine={false} tickLine={false} width={34} />
                    <Tooltip content={<TimelineTooltip currency={currency} lang={lang} />} />
                    <ReferenceLine yAxisId="price" y={detail.optimum.price} stroke={T.amber as any} strokeDasharray="5 4" />
                    <Line yAxisId="price" dataKey="price" name={t('Fiyat')} stroke={T.blue as any} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} isAnimationActive={false} />
                    <Line yAxisId="demand" dataKey="demand" name={t('Talep')} stroke={T.green as any} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </SectionCard>

              <SectionCard title={t('Fiyat Optimizasyon Eğrisi')} sub={t('Gözlenen fiyat aralığında gelir ve talep tepkisi.')} style={{ marginBottom: 14 }} pad={isMobile ? 14 : 18}
                right={<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <LegendDot line color={T.blue} label={t('Gelir')} /><LegendDot line dashed color={T.green} label={t('Talep')} /><LegendDot line dashed color={T.amber} label={t('Optimum')} />
                </div>}>
                <ResponsiveContainer width="100%" height={chartH}>
                  <ComposedChart data={detail.optimization_curve} margin={{ top: 22, right: isMobile ? 0 : 10, bottom: 0, left: isMobile ? -14 : 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.divider as any} vertical={false} />
                    <XAxis dataKey="price" type="number" domain={['auto', 'auto']} tickFormatter={(v) => money(v, currency)} tick={axisTick} axisLine={false} tickLine={false} minTickGap={16} />
                    <YAxis yAxisId="revenue" tickFormatter={(v) => money(v, currency)} tick={{ ...axisTick, fill: T.blueText as any }} axisLine={false} tickLine={false} width={isMobile ? 54 : 66} />
                    <YAxis yAxisId="demand" orientation="right" tick={{ ...axisTick, fill: T.greenText as any }} axisLine={false} tickLine={false} width={34} />
                    <Tooltip content={<CurveTooltip currency={currency} t={t} />} />
                    <ReferenceLine yAxisId="revenue" x={detail.optimum.price} stroke={T.amber as any} strokeWidth={1.5} strokeDasharray="5 4" label={{ value: `${t('Optimum')}: ${money(detail.optimum.price, currency)}`, position: 'top', fontSize: 10.5, fontWeight: 700, fill: T.amberDeepText as any }} />
                    <Line yAxisId="revenue" dataKey="revenue" name={t('Gelir')} stroke={T.blue as any} strokeWidth={2.5} dot={false} isAnimationActive={false} />
                    <Line yAxisId="demand" dataKey="demand" name={t('Talep')} stroke={T.green as any} strokeWidth={2} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
                <details className="gr-details" style={{ background: T.blueTint, borderRadius: 12, padding: '10px 14px', marginTop: 8 }}>
                  <summary style={{ fontSize: 12.5, fontWeight: 800, color: T.blueText, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    {t('Bu grafik nasıl okunur?')}
                    <span className="gr-chev" style={{ display: 'flex', transition: 'transform .15s' }}><Icon.chevron size={14} /></span>
                  </summary>
                  <div style={{ fontSize: 12.5, color: T.text, lineHeight: 1.6, marginTop: 8 }}>
                    {t('Mavi eğri, farklı fiyatlarda tahmini aylık geliri; yeşil kesikli çizgi tahmini talebi gösterir. Turuncu çizgi, geliri en yükselten fiyattır (±güvenlik bandı içinde).')}
                  </div>
                </details>
              </SectionCard>

              <SectionCard title={t('Aylık Esneklik')} sub={t('Aydan aya fiyat ve talep değişimi.')} style={{ marginBottom: 14 }} pad={isMobile ? 14 : 18}>
                {isMobile ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {detail.monthly_elasticity.map(row => (
                      <div key={row.month} style={{ background: T.subtleBg, borderRadius: 12, padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{monthLabel(row.month, lang)}</span>
                          <span style={{ fontSize: 12, fontWeight: 800, fontFamily: MONO, color: row.elasticity < 0 ? T.greenText : row.elasticity > 0 ? T.redText : T.textDim }}>β {row.elasticity.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 12, color: T.textMid }}>
                          <span>{t('Fiyat')}: <b style={{ color: T.text, fontFamily: MONO }}>{money(row.price, currency)}</b></span>
                          <span>{t('Δ Fiyat')}: <b style={{ fontFamily: MONO, color: row.d_price_pct > 0 ? T.greenText : row.d_price_pct < 0 ? T.redText : T.textDim }}>{row.d_price_pct > 0 ? '↑' : row.d_price_pct < 0 ? '↓' : ''} {Math.abs(row.d_price_pct)}%</b></span>
                          <span>{t('Talep')}: <b style={{ color: T.text, fontFamily: MONO }}>{row.demand}</b></span>
                          <span>{t('Δ Talep')}: <b style={{ fontFamily: MONO, color: row.d_demand_pct > 0 ? T.greenText : row.d_demand_pct < 0 ? T.redText : T.textDim }}>{row.d_demand_pct > 0 ? '↑' : row.d_demand_pct < 0 ? '↓' : ''} {Math.abs(row.d_demand_pct)}%</b></span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="gr-thin" style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 560 }}>
                      <thead>
                        <tr style={{ color: T.textDim, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {[t('Ay'), t('Fiyat'), t('Talep'), t('Gelir'), t('Δ Fiyat'), t('Δ Talep'), t('Esneklik')].map((h, i) => (
                            <th key={h} style={{ padding: '8px 10px', textAlign: i === 0 ? 'left' : 'right', fontWeight: 800, borderBottom: `1px solid ${T.border}` }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detail.monthly_elasticity.map(row => (
                          <tr key={row.month} className="gr-row" style={{ borderBottom: `1px solid ${T.divider}` }}>
                            <td style={{ padding: '9px 10px', color: T.text, fontWeight: 700 }}>{monthLabel(row.month, lang)}</td>
                            <td style={{ padding: '9px 10px', textAlign: 'right', fontFamily: MONO, color: T.textMid }}>{money(row.price, currency)}</td>
                            <td style={{ padding: '9px 10px', textAlign: 'right', fontFamily: MONO, color: T.textMid }}>{row.demand}</td>
                            <td style={{ padding: '9px 10px', textAlign: 'right', fontFamily: MONO, color: T.textMid }}>{money(row.revenue, currency)}</td>
                            <td style={{ padding: '9px 10px', textAlign: 'right', fontFamily: MONO, fontWeight: 700, color: row.d_price_pct > 0 ? T.greenText : row.d_price_pct < 0 ? T.redText : T.textDim }}>{row.d_price_pct === 0 ? '0.0%' : `${row.d_price_pct > 0 ? '↑' : '↓'} ${Math.abs(row.d_price_pct)}%`}</td>
                            <td style={{ padding: '9px 10px', textAlign: 'right', fontFamily: MONO, fontWeight: 700, color: row.d_demand_pct > 0 ? T.greenText : row.d_demand_pct < 0 ? T.redText : T.textDim }}>{row.d_demand_pct === 0 ? '0.0%' : `${row.d_demand_pct > 0 ? '↑' : '↓'} ${Math.abs(row.d_demand_pct)}%`}</td>
                            <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                              <span style={{ fontFamily: MONO, fontWeight: 800, fontSize: 12, color: row.elasticity < 0 ? T.greenText : row.elasticity > 0 ? T.redText : T.textDim,
                                background: row.elasticity < 0 ? T.greenTint : row.elasticity > 0 ? T.redTint : T.subtleBg, borderRadius: 999, padding: '2px 9px' }}>{row.elasticity.toFixed(2)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </SectionCard>

              {/* Teknik detaylar: varsayılan kapalı, merak eden açar */}
              <details className="gr-details" style={{ ...cardStyle, padding: isMobile ? 14 : '16px 18px' }}>
                <summary style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ width: 32, height: 32, borderRadius: 10, background: T.purpleTint, color: T.purpleText, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon.layers size={16} /></span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14.5, fontWeight: 800, color: T.text }}>{t('Model nasıl çalışıyor?')}</span>
                    <span style={{ display: 'block', fontSize: 12, color: T.textDim, marginTop: 2 }}>{t('Gerçek aylık noktalar ve model eğrisi, adım adım hesaplama ve metodoloji.')}</span>
                  </span>
                  <span className="gr-chev" style={{ display: 'flex', color: T.textDim, transition: 'transform .15s' }}><Icon.chevron size={16} /></span>
                </summary>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20, marginTop: 16 }}>
                  <div style={{ minWidth: 0 }}>
                    <ResponsiveContainer width="100%" height={220}>
                      <ComposedChart margin={{ top: 16, right: 10, bottom: 0, left: isMobile ? -14 : 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={T.divider as any} vertical={false} />
                        <XAxis dataKey="price" type="number" domain={['auto', 'auto']} tick={axisTick} axisLine={false} tickLine={false} tickFormatter={(v) => money(v, currency)} minTickGap={16} />
                        <YAxis dataKey="revenue" type="number" tick={axisTick} axisLine={false} tickLine={false} width={54} tickFormatter={(v) => money(v, currency)} />
                        <Tooltip content={<CurveTooltip currency={currency} t={t} />} />
                        <ReferenceLine x={detail.model_visualization.optimum_price} stroke={T.amber as any} strokeDasharray="4 3" label={{ value: t('Optimum'), position: 'top', fontSize: 10, fill: T.amberDeepText as any }} />
                        <ReferenceLine x={detail.model_visualization.current_price} stroke={T.textDim as any} strokeDasharray="2 3" label={{ value: t('Mevcut'), position: 'insideTopRight', fontSize: 9.5, fill: T.textDim as any }} />
                        <Scatter data={detail.model_visualization.points} fill={T.blue as any} fillOpacity={0.7} />
                        <Line data={detail.model_visualization.curve} dataKey="revenue" stroke={T.blue as any} strokeWidth={2} dot={false} isAnimationActive={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {[
                      [t('Veri Noktaları:'), t('Her nokta bir ayın gerçek fiyat ve gelir performansını temsil eder.')],
                      [t('Eğri Uydurma:'), t('LOESS (Yerel Regresyon) dağılımdan geçen düzgün bir eğilim çizer, "bu fiyatı denesek ne olurdu?" sorusuna cevap arar.')],
                      [t('Optimum:'), t('Gelir eğrisinin tepe noktası, geliri maksimize eden fiyat noktasıdır (±%{pct} güvenlik bandı içinde).').replace('{pct}', String(detail.optimum.guardrail_pct))],
                    ].map(([h, b], i) => (
                      <li key={i} style={{ display: 'flex', gap: 10, fontSize: 12.5, color: T.textMid, lineHeight: 1.55 }}>
                        <span style={{ width: 22, height: 22, borderRadius: '50%', background: T.blueTint, color: T.blueText, fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
                        <span><strong style={{ color: T.text }}>{h}</strong> {b}</span>
                      </li>
                    ))}
                  </ol>
                </div>
                <div style={{ borderTop: `1px solid ${T.divider}`, marginTop: 16, paddingTop: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: T.text, marginBottom: 8 }}>{t('Metodoloji')}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '6px 24px', fontSize: 12.5, color: T.textMid, lineHeight: 1.7 }}>
                    <div><strong style={{ color: T.text }}>{t('Veri:')}</strong> {t('Aylık fiyat = Gelir / Adet, talep = satılan adet.')}</div>
                    <div><strong style={{ color: T.text }}>{t('LOESS:')}</strong> {t('Tricube çekirdek ve yenilik ağırlıklandırmasıyla (λ={lambda}) yerel doğrusal regresyon.').replace('{lambda}', String(detail.methodology.lambda_recency))}</div>
                    <div><strong style={{ color: T.text }}>{t('Esneklik:')}</strong> {t('Log-log OLS —')} β = Cov(ln P, ln Q) / Var(ln P)</div>
                    <div><strong style={{ color: T.text }}>{t('Güvenlik Bantları:')}</strong> {t('Son ay geliri P75 üzerindeyse ±%10, değilse ±%20.')}</div>
                    <div><strong style={{ color: T.text }}>{t('Optimum Fiyat:')}</strong> {t('(P, R=P×Q) üzerinde LOESS düzgünleştirme, ±%10-20 güvenlik bandı içinde tepe.')}</div>
                    <div><strong style={{ color: T.text }}>{t('Güven:')}</strong> {t('Negatif esneklik = beklenen (yeşil). Pozitif = anomali/sezonluk (kırmızı).')}</div>
                    <div><strong style={{ color: T.text }}>{t('Etki:')}</strong> (R(p*) − R_mevcut) / R_mevcut</div>
                    <div><strong style={{ color: T.text }}>{t('Not:')}</strong> {t('Geçmiş performans gelecek sonuçları garanti etmez.')}</div>
                  </div>
                </div>
              </details>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
